import type { Language } from "../../types/i18n";

/*
 * UI strings for one area of the app, merged into `translations` (see
 * ./index.ts) so components read them through `useI18nStore().t` like every
 * other key. English is the reference; the other languages must carry the
 * same keys, which the record type enforces.
 *
 * Area: the Medical Conditions, Treatment History and Medical Procedures
 * cards on the health page (prefixes `cond`, `treat`, `proc`).
 */
const en = {
  // Medical conditions
  condTitle: "Medical Conditions",
  condEmpty: "No medical conditions documented",
  condAdd: "Add condition",
  condRemove: "Remove condition",
  condFieldCondition: "Medical condition",
  condCustomPlaceholder: "Enter condition",
  condIcd: "ICD",
  condFieldCategory: "Category",
  condCategoryProblemListItem: "Chronic condition",
  condCategoryEncounterDiagnosis: "Acute diagnosis",
  condCategoryHealthConcern: "Health risk",
  condBadgeChronic: "Chronic",
  condBadgeAcute: "Acute",
  condBadgeRisk: "Risk",
  condFieldClinicalStatus: "Clinical status",
  condStatusActive: "Active",
  condStatusInactive: "Inactive",
  condStatusResolved: "Resolved",
  condStatusRemission: "In remission",
  condStatusRecurrence: "Recurrence",
  condStatusRelapse: "Relapse",
  condFieldVerificationStatus: "Verification status",
  condVerificationConfirmed: "Confirmed",
  condVerificationUnconfirmed: "Unconfirmed",
  condVerificationProvisional: "Provisional",
  condVerificationDifferential: "Differential diagnosis",
  condVerificationRefuted: "Refuted",
  condVerificationEnteredInError: "Entered in error",
  condFieldSeverity: "Severity (optional)",
  condSeverityNone: "Not specified",
  condSeverityMild: "Mild",
  condSeverityModerate: "Moderate",
  condSeveritySevere: "Severe",
  condFieldOnset: "First diagnosed (optional)",
  condFieldAbatement: "Resolved/ended on (optional)",
  condFieldRecorder: "Treating physician (optional)",
  condRecorderPlaceholder: "Dr. Smith",
  condFieldNote: "Additional notes (optional)",
  condNotePlaceholder: "Additional information about the condition",
  condSince: "Since: {date}",
  condUntil: "Until: {date}",
  condTreatingPhysician: "Treating physician: {name}",
  condNameDiabetes: "Diabetes mellitus",
  condNameHypertension: "High blood pressure (hypertension)",
  condNameAsthma: "Asthma",
  condNameCoronaryArteryDisease: "Coronary artery disease",
  condNameGastritis: "Gastritis",
  condNameBackPain: "Back pain",
  condNameDepression: "Depression",
  condNameAnxietyDisorder: "Anxiety disorder",
  condNameHighCholesterol: "High cholesterol",
  condNameRheumatoidArthritis: "Rheumatoid arthritis",
  condNameCopd: "COPD",
  condNameKidneyDisease: "Chronic kidney disease",
  condNameConstipation: "Constipation",
  condNameMyopia: "Nearsightedness (myopia)",
  condNameIronDeficiency: "Iron deficiency",
  condNameShingles: "Shingles",

  // Treatment history
  treatFieldType: "Treatment type",
  treatTypeProcedure: "Examination",
  treatTypeTherapy: "Therapy",
  treatTypeSurgery: "Surgery",
  treatTypeMedication: "Medication",
  treatStatusCompleted: "Completed",
  treatStatusInProgress: "In progress",
  treatStatusPlanned: "Planned",
  treatStatusCancelled: "Cancelled",
  treatFieldTreatment: "Treatment",
  treatCustomPlaceholder: "Enter treatment",
  treatFieldDate: "Date",
  treatFieldPerformer: "Performing physician/therapist",
  treatPerformerPlaceholder: "Dr. Smith",
  treatFieldLocation: "Treatment location (optional)",
  treatLocationPlaceholder: "Hospital, doctor's office, etc.",
  treatFieldOutcome: "Result/outcome (optional)",
  treatOutcomePlaceholder: "Treatment result, findings, etc.",
  treatFieldNotes: "Additional notes (optional)",
  treatNotesPlaceholder: "Additional information",
  treatRemove: "Remove treatment",
  treatAdd: "Add treatment",
  treatOutcomeLabel: "Outcome:",
  treatNameBloodPressureCheck: "Blood pressure check",
  treatNameRoutineCheckup: "Routine checkup",
  treatNameVaccination: "Vaccination",
  treatNameConsultation: "Consultation",
  treatNameBloodTest: "Blood test",
  treatNameXray: "X-ray",
  treatNameUltrasound: "Ultrasound",
  treatNamePhysicalTherapy: "Physical therapy",
  treatNamePsychotherapy: "Psychotherapy",
  treatNameOccupationalTherapy: "Occupational therapy",
  treatNameRespiratoryTherapy: "Respiratory therapy",
  treatNameMassage: "Massage",
  treatNameMinorSurgery: "Minor surgery",
  treatNameArthroscopy: "Arthroscopy",
  treatNameAppendectomy: "Appendectomy",
  treatNameHeartSurgery: "Heart surgery",
  treatNameAcetaminophenTherapy: "Acetaminophen (paracetamol) therapy",
  treatNameAntibioticCourse: "Course of antibiotics",
  treatNameBloodPressureMedication: "Blood pressure medication",

  // Medical procedures
  procFieldCategory: "Category",
  procCategoryOutpatient: "Outpatient",
  procCategoryInpatient: "Inpatient",
  procCategoryAmbulatory: "Day clinic",
  procCategoryEmergency: "Emergency",
  procStatusCompleted: "Completed",
  procStatusInProgress: "In progress",
  procStatusPreparation: "In preparation",
  procStatusNotDone: "Not done",
  procStatusOnHold: "On hold",
  procStatusStopped: "Stopped",
  procStatusEnteredInError: "Entered in error",
  procStatusUnknown: "Unknown",
  procFieldProcedure: "Procedure/examination",
  procCustomPlaceholder: "Enter procedure/examination",
  procFieldDate: "Date",
  procFieldPerformer: "Performing physician/clinic",
  procPerformerPlaceholder: "Dr. Smith / University Hospital",
  procFieldLocation: "Location (optional)",
  procLocationPlaceholder: "Hospital, doctor's office, clinic",
  procFieldReason: "Indication/reason (optional)",
  procReasonPlaceholder: "Reason for the procedure/examination",
  procFieldOutcome: "Result/findings (optional)",
  procOutcomePlaceholder: "Findings, result of the examination/procedure",
  procFieldComplications: "Complications (optional)",
  procComplicationsPlaceholder: "Complications that occurred",
  procFieldNotes: "Additional notes (optional)",
  procNotesPlaceholder: "Additional information",
  procRemove: "Remove procedure",
  procAdd: "Add procedure",
  procBadgeUpcoming: "Scheduled",
  procBadgeNew: "New",
  procNoDate: "No date",
  procLocationLabel: "Location:",
  procIndicationLabel: "Indication:",
  procOutcomeLabel: "Result:",
  procComplicationsLabel: "Complications:",
  procNameChestXray: "Chest X-ray",
  procNameBloodPanel: "Comprehensive blood panel",
  procNameEcg: "ECG",
  procNameAbdominalUltrasound: "Abdominal ultrasound",
  procNamePreventiveCheckup: "Preventive checkup",
  procNameMammography: "Mammography",
  procNamePapSmear: "Pap smear",
  procNameColorectalScreening: "Colorectal cancer screening",
  procNamePhysicalTherapy: "Physical therapy",
  procNameVaccination: "Vaccination",
  procNameWoundCare: "Wound care",
  procNameJointAspiration: "Joint aspiration",
  procNamePainInjection: "Pain management/injection",
  procNameColonoscopy: "Colonoscopy",
  procNameAppendectomy: "Appendectomy",
  procNameGallbladderSurgery: "Gallbladder surgery",
  procNameKneeArthroscopy: "Knee arthroscopy",
  procNameCataractSurgery: "Cataract surgery",
  procNameMastectomy: "Mastectomy",
  procNameHeadCt: "Head CT",
  procNameSpineMri: "Spine MRI",
  procNameEmergencyBloodTest: "Emergency blood test",
};

export type HealthConditionsStrings = typeof en;

const de: HealthConditionsStrings = {
  condTitle: "Vorerkrankungen",
  condEmpty: "Keine Vorerkrankungen dokumentiert",
  condAdd: "Neue Erkrankung hinzufügen",
  condRemove: "Erkrankung entfernen",
  condFieldCondition: "Erkrankung",
  condCustomPlaceholder: "Erkrankung eingeben",
  condIcd: "ICD",
  condFieldCategory: "Kategorie",
  condCategoryProblemListItem: "Chronische Erkrankung",
  condCategoryEncounterDiagnosis: "Akute Diagnose",
  condCategoryHealthConcern: "Gesundheitsrisiko",
  condBadgeChronic: "Chronisch",
  condBadgeAcute: "Akut",
  condBadgeRisk: "Risiko",
  condFieldClinicalStatus: "Klinischer Status",
  condStatusActive: "Aktiv",
  condStatusInactive: "Inaktiv",
  condStatusResolved: "Ausgeheilt",
  condStatusRemission: "In Remission",
  condStatusRecurrence: "Wiederauftreten",
  condStatusRelapse: "Rückfall",
  condFieldVerificationStatus: "Verifikationsstatus",
  condVerificationConfirmed: "Bestätigt",
  condVerificationUnconfirmed: "Unbestätigt",
  condVerificationProvisional: "Vorläufig",
  condVerificationDifferential: "Differenzialdiagnose",
  condVerificationRefuted: "Widerlegt",
  condVerificationEnteredInError: "Fehlerhaft erfasst",
  condFieldSeverity: "Schweregrad (optional)",
  condSeverityNone: "Nicht angegeben",
  condSeverityMild: "Leicht",
  condSeverityModerate: "Mittel",
  condSeveritySevere: "Schwer",
  condFieldOnset: "Erstmals diagnostiziert (optional)",
  condFieldAbatement: "Ausgeheilt/beendet am (optional)",
  condFieldRecorder: "Behandelnder Arzt (optional)",
  condRecorderPlaceholder: "Dr. Müller",
  condFieldNote: "Zusätzliche Notizen (optional)",
  condNotePlaceholder: "Zusätzliche Informationen zur Erkrankung",
  condSince: "Seit: {date}",
  condUntil: "Bis: {date}",
  condTreatingPhysician: "Behandelnder Arzt: {name}",
  condNameDiabetes: "Diabetes mellitus",
  condNameHypertension: "Bluthochdruck",
  condNameAsthma: "Asthma bronchiale",
  condNameCoronaryArteryDisease: "Koronare Herzkrankheit",
  condNameGastritis: "Gastritis",
  condNameBackPain: "Rückenschmerzen",
  condNameDepression: "Depression",
  condNameAnxietyDisorder: "Angststörung",
  condNameHighCholesterol: "Erhöhte Cholesterinwerte",
  condNameRheumatoidArthritis: "Rheumatoide Arthritis",
  condNameCopd: "COPD",
  condNameKidneyDisease: "Niereninsuffizienz",
  condNameConstipation: "Verstopfung",
  condNameMyopia: "Kurzsichtigkeit",
  condNameIronDeficiency: "Eisenmangel",
  condNameShingles: "Gürtelrose",

  treatFieldType: "Behandlungsart",
  treatTypeProcedure: "Untersuchung",
  treatTypeTherapy: "Therapie",
  treatTypeSurgery: "Operation",
  treatTypeMedication: "Medikation",
  treatStatusCompleted: "Abgeschlossen",
  treatStatusInProgress: "Laufend",
  treatStatusPlanned: "Geplant",
  treatStatusCancelled: "Abgebrochen",
  treatFieldTreatment: "Behandlung",
  treatCustomPlaceholder: "Behandlung eingeben",
  treatFieldDate: "Datum",
  treatFieldPerformer: "Durchführender Arzt/Therapeut",
  treatPerformerPlaceholder: "Dr. Müller",
  treatFieldLocation: "Behandlungsort (optional)",
  treatLocationPlaceholder: "Krankenhaus, Praxis usw.",
  treatFieldOutcome: "Ergebnis/Verlauf (optional)",
  treatOutcomePlaceholder: "Behandlungsergebnis, Befund usw.",
  treatFieldNotes: "Zusätzliche Notizen (optional)",
  treatNotesPlaceholder: "Zusätzliche Informationen",
  treatRemove: "Behandlung entfernen",
  treatAdd: "Neue Behandlung hinzufügen",
  treatOutcomeLabel: "Ergebnis:",
  treatNameBloodPressureCheck: "Blutdruckmessung",
  treatNameRoutineCheckup: "Routineuntersuchung",
  treatNameVaccination: "Impfung",
  treatNameConsultation: "Beratungsgespräch",
  treatNameBloodTest: "Blutuntersuchung",
  treatNameXray: "Röntgenuntersuchung",
  treatNameUltrasound: "Ultraschalluntersuchung",
  treatNamePhysicalTherapy: "Physiotherapie",
  treatNamePsychotherapy: "Psychotherapie",
  treatNameOccupationalTherapy: "Ergotherapie",
  treatNameRespiratoryTherapy: "Atemtherapie",
  treatNameMassage: "Massage",
  treatNameMinorSurgery: "Kleiner chirurgischer Eingriff",
  treatNameArthroscopy: "Arthroskopie",
  treatNameAppendectomy: "Blinddarmoperation",
  treatNameHeartSurgery: "Herzoperation",
  treatNameAcetaminophenTherapy: "Paracetamol-Therapie",
  treatNameAntibioticCourse: "Antibiotikakur",
  treatNameBloodPressureMedication: "Blutdruckmedikation",

  procFieldCategory: "Kategorie",
  procCategoryOutpatient: "Ambulant",
  procCategoryInpatient: "Stationär",
  procCategoryAmbulatory: "Tagesklinik",
  procCategoryEmergency: "Notfall",
  procStatusCompleted: "Abgeschlossen",
  procStatusInProgress: "Laufend",
  procStatusPreparation: "In Vorbereitung",
  procStatusNotDone: "Nicht durchgeführt",
  procStatusOnHold: "Unterbrochen",
  procStatusStopped: "Abgebrochen",
  procStatusEnteredInError: "Fehlerhaft erfasst",
  procStatusUnknown: "Unbekannt",
  procFieldProcedure: "Eingriff/Untersuchung",
  procCustomPlaceholder: "Eingriff/Untersuchung eingeben",
  procFieldDate: "Datum",
  procFieldPerformer: "Durchführender Arzt/Klinik",
  procPerformerPlaceholder: "Dr. Müller / Universitätsklinikum",
  procFieldLocation: "Ort der Durchführung (optional)",
  procLocationPlaceholder: "Krankenhaus, Praxis, Klinik",
  procFieldReason: "Indikation/Grund (optional)",
  procReasonPlaceholder: "Grund für den Eingriff/die Untersuchung",
  procFieldOutcome: "Ergebnis/Befund (optional)",
  procOutcomePlaceholder: "Befund, Ergebnis der Untersuchung/des Eingriffs",
  procFieldComplications: "Komplikationen (optional)",
  procComplicationsPlaceholder: "Aufgetretene Komplikationen",
  procFieldNotes: "Zusätzliche Notizen (optional)",
  procNotesPlaceholder: "Zusätzliche Informationen",
  procRemove: "Eingriff entfernen",
  procAdd: "Neuen Eingriff hinzufügen",
  procBadgeUpcoming: "Geplant",
  procBadgeNew: "Neu",
  procNoDate: "Kein Datum",
  procLocationLabel: "Ort:",
  procIndicationLabel: "Indikation:",
  procOutcomeLabel: "Ergebnis:",
  procComplicationsLabel: "Komplikationen:",
  procNameChestXray: "Röntgen Thorax",
  procNameBloodPanel: "Großes Blutbild",
  procNameEcg: "EKG",
  procNameAbdominalUltrasound: "Ultraschall Abdomen",
  procNamePreventiveCheckup: "Vorsorgeuntersuchung",
  procNameMammography: "Mammographie",
  procNamePapSmear: "PAP-Abstrich",
  procNameColorectalScreening: "Darmkrebs-Screening",
  procNamePhysicalTherapy: "Physiotherapie",
  procNameVaccination: "Impfung",
  procNameWoundCare: "Wundversorgung",
  procNameJointAspiration: "Gelenkpunktion",
  procNamePainInjection: "Schmerztherapie/Injektion",
  procNameColonoscopy: "Darmspiegelung",
  procNameAppendectomy: "Blinddarmoperation",
  procNameGallbladderSurgery: "Gallenblasenoperation",
  procNameKneeArthroscopy: "Arthroskopie Knie",
  procNameCataractSurgery: "Grauer-Star-Operation",
  procNameMastectomy: "Mastektomie",
  procNameHeadCt: "CT Kopf",
  procNameSpineMri: "MRT Wirbelsäule",
  procNameEmergencyBloodTest: "Notfall-Blutuntersuchung",
};

const fr: HealthConditionsStrings = {
  condTitle: "Antécédents médicaux",
  condEmpty: "Aucune affection médicale documentée",
  condAdd: "Ajouter une affection",
  condRemove: "Supprimer l'affection",
  condFieldCondition: "Affection médicale",
  condCustomPlaceholder: "Saisir l'affection",
  condIcd: "CIM",
  condFieldCategory: "Catégorie",
  condCategoryProblemListItem: "Affection chronique",
  condCategoryEncounterDiagnosis: "Diagnostic aigu",
  condCategoryHealthConcern: "Risque pour la santé",
  condBadgeChronic: "Chronique",
  condBadgeAcute: "Aigu",
  condBadgeRisk: "Risque",
  condFieldClinicalStatus: "Statut clinique",
  condStatusActive: "Active",
  condStatusInactive: "Inactive",
  condStatusResolved: "Guérie",
  condStatusRemission: "En rémission",
  condStatusRecurrence: "Récurrence",
  condStatusRelapse: "Rechute",
  condFieldVerificationStatus: "Statut de vérification",
  condVerificationConfirmed: "Confirmé",
  condVerificationUnconfirmed: "Non confirmé",
  condVerificationProvisional: "Provisoire",
  condVerificationDifferential: "Diagnostic différentiel",
  condVerificationRefuted: "Réfuté",
  condVerificationEnteredInError: "Saisi par erreur",
  condFieldSeverity: "Gravité (facultatif)",
  condSeverityNone: "Non précisée",
  condSeverityMild: "Légère",
  condSeverityModerate: "Modérée",
  condSeveritySevere: "Sévère",
  condFieldOnset: "Premier diagnostic (facultatif)",
  condFieldAbatement: "Guérie/terminée le (facultatif)",
  condFieldRecorder: "Médecin traitant (facultatif)",
  condRecorderPlaceholder: "Dr Martin",
  condFieldNote: "Notes complémentaires (facultatif)",
  condNotePlaceholder: "Informations complémentaires sur l'affection",
  condSince: "Depuis : {date}",
  condUntil: "Jusqu'au : {date}",
  condTreatingPhysician: "Médecin traitant : {name}",
  condNameDiabetes: "Diabète sucré",
  condNameHypertension: "Hypertension artérielle",
  condNameAsthma: "Asthme",
  condNameCoronaryArteryDisease: "Maladie coronarienne",
  condNameGastritis: "Gastrite",
  condNameBackPain: "Mal de dos",
  condNameDepression: "Dépression",
  condNameAnxietyDisorder: "Trouble anxieux",
  condNameHighCholesterol: "Cholestérol élevé",
  condNameRheumatoidArthritis: "Polyarthrite rhumatoïde",
  condNameCopd: "BPCO",
  condNameKidneyDisease: "Insuffisance rénale chronique",
  condNameConstipation: "Constipation",
  condNameMyopia: "Myopie",
  condNameIronDeficiency: "Carence en fer",
  condNameShingles: "Zona",

  treatFieldType: "Type de traitement",
  treatTypeProcedure: "Examen",
  treatTypeTherapy: "Thérapie",
  treatTypeSurgery: "Chirurgie",
  treatTypeMedication: "Médication",
  treatStatusCompleted: "Terminé",
  treatStatusInProgress: "En cours",
  treatStatusPlanned: "Prévu",
  treatStatusCancelled: "Annulé",
  treatFieldTreatment: "Traitement",
  treatCustomPlaceholder: "Saisir le traitement",
  treatFieldDate: "Date",
  treatFieldPerformer: "Médecin/thérapeute",
  treatPerformerPlaceholder: "Dr Martin",
  treatFieldLocation: "Lieu du traitement (facultatif)",
  treatLocationPlaceholder: "Hôpital, cabinet médical, etc.",
  treatFieldOutcome: "Résultat/évolution (facultatif)",
  treatOutcomePlaceholder: "Résultat du traitement, observations, etc.",
  treatFieldNotes: "Notes complémentaires (facultatif)",
  treatNotesPlaceholder: "Informations complémentaires",
  treatRemove: "Supprimer le traitement",
  treatAdd: "Ajouter un traitement",
  treatOutcomeLabel: "Résultat :",
  treatNameBloodPressureCheck: "Mesure de la tension artérielle",
  treatNameRoutineCheckup: "Examen de routine",
  treatNameVaccination: "Vaccination",
  treatNameConsultation: "Consultation",
  treatNameBloodTest: "Analyse de sang",
  treatNameXray: "Radiographie",
  treatNameUltrasound: "Échographie",
  treatNamePhysicalTherapy: "Kinésithérapie",
  treatNamePsychotherapy: "Psychothérapie",
  treatNameOccupationalTherapy: "Ergothérapie",
  treatNameRespiratoryTherapy: "Kinésithérapie respiratoire",
  treatNameMassage: "Massage",
  treatNameMinorSurgery: "Petite chirurgie",
  treatNameArthroscopy: "Arthroscopie",
  treatNameAppendectomy: "Appendicectomie",
  treatNameHeartSurgery: "Chirurgie cardiaque",
  treatNameAcetaminophenTherapy: "Traitement au paracétamol",
  treatNameAntibioticCourse: "Traitement antibiotique",
  treatNameBloodPressureMedication: "Traitement antihypertenseur",

  procFieldCategory: "Catégorie",
  procCategoryOutpatient: "Ambulatoire",
  procCategoryInpatient: "Hospitalisation",
  procCategoryAmbulatory: "Hôpital de jour",
  procCategoryEmergency: "Urgence",
  procStatusCompleted: "Terminé",
  procStatusInProgress: "En cours",
  procStatusPreparation: "En préparation",
  procStatusNotDone: "Non réalisé",
  procStatusOnHold: "Suspendu",
  procStatusStopped: "Arrêté",
  procStatusEnteredInError: "Saisi par erreur",
  procStatusUnknown: "Inconnu",
  procFieldProcedure: "Intervention/examen",
  procCustomPlaceholder: "Saisir l'intervention/l'examen",
  procFieldDate: "Date",
  procFieldPerformer: "Médecin/établissement",
  procPerformerPlaceholder: "Dr Martin / CHU",
  procFieldLocation: "Lieu (facultatif)",
  procLocationPlaceholder: "Hôpital, cabinet médical, clinique",
  procFieldReason: "Indication/motif (facultatif)",
  procReasonPlaceholder: "Motif de l'intervention/de l'examen",
  procFieldOutcome: "Résultat/conclusions (facultatif)",
  procOutcomePlaceholder: "Conclusions, résultat de l'examen/de l'intervention",
  procFieldComplications: "Complications (facultatif)",
  procComplicationsPlaceholder: "Complications survenues",
  procFieldNotes: "Notes complémentaires (facultatif)",
  procNotesPlaceholder: "Informations complémentaires",
  procRemove: "Supprimer l'intervention",
  procAdd: "Ajouter une intervention",
  procBadgeUpcoming: "Prévu",
  procBadgeNew: "Nouveau",
  procNoDate: "Pas de date",
  procLocationLabel: "Lieu :",
  procIndicationLabel: "Indication :",
  procOutcomeLabel: "Résultat :",
  procComplicationsLabel: "Complications :",
  procNameChestXray: "Radiographie thoracique",
  procNameBloodPanel: "Bilan sanguin complet",
  procNameEcg: "ECG",
  procNameAbdominalUltrasound: "Échographie abdominale",
  procNamePreventiveCheckup: "Bilan de santé préventif",
  procNameMammography: "Mammographie",
  procNamePapSmear: "Frottis cervical",
  procNameColorectalScreening: "Dépistage du cancer colorectal",
  procNamePhysicalTherapy: "Kinésithérapie",
  procNameVaccination: "Vaccination",
  procNameWoundCare: "Soins de plaie",
  procNameJointAspiration: "Ponction articulaire",
  procNamePainInjection: "Traitement de la douleur/injection",
  procNameColonoscopy: "Coloscopie",
  procNameAppendectomy: "Appendicectomie",
  procNameGallbladderSurgery: "Chirurgie de la vésicule biliaire",
  procNameKneeArthroscopy: "Arthroscopie du genou",
  procNameCataractSurgery: "Chirurgie de la cataracte",
  procNameMastectomy: "Mastectomie",
  procNameHeadCt: "Scanner cérébral",
  procNameSpineMri: "IRM de la colonne vertébrale",
  procNameEmergencyBloodTest: "Analyse de sang en urgence",
};

const es: HealthConditionsStrings = {
  condTitle: "Enfermedades previas",
  condEmpty: "No hay enfermedades documentadas",
  condAdd: "Añadir enfermedad",
  condRemove: "Eliminar enfermedad",
  condFieldCondition: "Enfermedad",
  condCustomPlaceholder: "Introducir enfermedad",
  condIcd: "CIE",
  condFieldCategory: "Categoría",
  condCategoryProblemListItem: "Enfermedad crónica",
  condCategoryEncounterDiagnosis: "Diagnóstico agudo",
  condCategoryHealthConcern: "Riesgo para la salud",
  condBadgeChronic: "Crónica",
  condBadgeAcute: "Aguda",
  condBadgeRisk: "Riesgo",
  condFieldClinicalStatus: "Estado clínico",
  condStatusActive: "Activa",
  condStatusInactive: "Inactiva",
  condStatusResolved: "Curada",
  condStatusRemission: "En remisión",
  condStatusRecurrence: "Recurrencia",
  condStatusRelapse: "Recaída",
  condFieldVerificationStatus: "Estado de verificación",
  condVerificationConfirmed: "Confirmado",
  condVerificationUnconfirmed: "No confirmado",
  condVerificationProvisional: "Provisional",
  condVerificationDifferential: "Diagnóstico diferencial",
  condVerificationRefuted: "Descartado",
  condVerificationEnteredInError: "Registrado por error",
  condFieldSeverity: "Gravedad (opcional)",
  condSeverityNone: "No especificada",
  condSeverityMild: "Leve",
  condSeverityModerate: "Moderada",
  condSeveritySevere: "Grave",
  condFieldOnset: "Diagnosticada por primera vez (opcional)",
  condFieldAbatement: "Curada/finalizada el (opcional)",
  condFieldRecorder: "Médico responsable (opcional)",
  condRecorderPlaceholder: "Dr. García",
  condFieldNote: "Notas adicionales (opcional)",
  condNotePlaceholder: "Información adicional sobre la enfermedad",
  condSince: "Desde: {date}",
  condUntil: "Hasta: {date}",
  condTreatingPhysician: "Médico responsable: {name}",
  condNameDiabetes: "Diabetes mellitus",
  condNameHypertension: "Hipertensión arterial",
  condNameAsthma: "Asma",
  condNameCoronaryArteryDisease: "Enfermedad coronaria",
  condNameGastritis: "Gastritis",
  condNameBackPain: "Dolor de espalda",
  condNameDepression: "Depresión",
  condNameAnxietyDisorder: "Trastorno de ansiedad",
  condNameHighCholesterol: "Colesterol alto",
  condNameRheumatoidArthritis: "Artritis reumatoide",
  condNameCopd: "EPOC",
  condNameKidneyDisease: "Insuficiencia renal crónica",
  condNameConstipation: "Estreñimiento",
  condNameMyopia: "Miopía",
  condNameIronDeficiency: "Déficit de hierro",
  condNameShingles: "Herpes zóster",

  treatFieldType: "Tipo de tratamiento",
  treatTypeProcedure: "Exploración",
  treatTypeTherapy: "Terapia",
  treatTypeSurgery: "Cirugía",
  treatTypeMedication: "Medicación",
  treatStatusCompleted: "Completado",
  treatStatusInProgress: "En curso",
  treatStatusPlanned: "Previsto",
  treatStatusCancelled: "Cancelado",
  treatFieldTreatment: "Tratamiento",
  treatCustomPlaceholder: "Introducir tratamiento",
  treatFieldDate: "Fecha",
  treatFieldPerformer: "Médico/terapeuta",
  treatPerformerPlaceholder: "Dr. García",
  treatFieldLocation: "Lugar del tratamiento (opcional)",
  treatLocationPlaceholder: "Hospital, consulta, etc.",
  treatFieldOutcome: "Resultado/evolución (opcional)",
  treatOutcomePlaceholder: "Resultado del tratamiento, hallazgos, etc.",
  treatFieldNotes: "Notas adicionales (opcional)",
  treatNotesPlaceholder: "Información adicional",
  treatRemove: "Eliminar tratamiento",
  treatAdd: "Añadir tratamiento",
  treatOutcomeLabel: "Resultado:",
  treatNameBloodPressureCheck: "Medición de la presión arterial",
  treatNameRoutineCheckup: "Revisión rutinaria",
  treatNameVaccination: "Vacunación",
  treatNameConsultation: "Consulta",
  treatNameBloodTest: "Análisis de sangre",
  treatNameXray: "Radiografía",
  treatNameUltrasound: "Ecografía",
  treatNamePhysicalTherapy: "Fisioterapia",
  treatNamePsychotherapy: "Psicoterapia",
  treatNameOccupationalTherapy: "Terapia ocupacional",
  treatNameRespiratoryTherapy: "Fisioterapia respiratoria",
  treatNameMassage: "Masaje",
  treatNameMinorSurgery: "Cirugía menor",
  treatNameArthroscopy: "Artroscopia",
  treatNameAppendectomy: "Apendicectomía",
  treatNameHeartSurgery: "Cirugía cardíaca",
  treatNameAcetaminophenTherapy: "Tratamiento con paracetamol",
  treatNameAntibioticCourse: "Tratamiento antibiótico",
  treatNameBloodPressureMedication: "Medicación para la tensión arterial",

  procFieldCategory: "Categoría",
  procCategoryOutpatient: "Ambulatorio",
  procCategoryInpatient: "Hospitalario",
  procCategoryAmbulatory: "Hospital de día",
  procCategoryEmergency: "Urgencias",
  procStatusCompleted: "Completado",
  procStatusInProgress: "En curso",
  procStatusPreparation: "En preparación",
  procStatusNotDone: "No realizado",
  procStatusOnHold: "En espera",
  procStatusStopped: "Interrumpido",
  procStatusEnteredInError: "Registrado por error",
  procStatusUnknown: "Desconocido",
  procFieldProcedure: "Intervención/exploración",
  procCustomPlaceholder: "Introducir intervención/exploración",
  procFieldDate: "Fecha",
  procFieldPerformer: "Médico/centro",
  procPerformerPlaceholder: "Dr. García / Hospital Universitario",
  procFieldLocation: "Lugar (opcional)",
  procLocationPlaceholder: "Hospital, consulta, clínica",
  procFieldReason: "Indicación/motivo (opcional)",
  procReasonPlaceholder: "Motivo de la intervención/exploración",
  procFieldOutcome: "Resultado/hallazgos (opcional)",
  procOutcomePlaceholder: "Hallazgos, resultado de la exploración/intervención",
  procFieldComplications: "Complicaciones (opcional)",
  procComplicationsPlaceholder: "Complicaciones surgidas",
  procFieldNotes: "Notas adicionales (opcional)",
  procNotesPlaceholder: "Información adicional",
  procRemove: "Eliminar intervención",
  procAdd: "Añadir intervención",
  procBadgeUpcoming: "Programado",
  procBadgeNew: "Nuevo",
  procNoDate: "Sin fecha",
  procLocationLabel: "Lugar:",
  procIndicationLabel: "Indicación:",
  procOutcomeLabel: "Resultado:",
  procComplicationsLabel: "Complicaciones:",
  procNameChestXray: "Radiografía de tórax",
  procNameBloodPanel: "Análisis de sangre completo",
  procNameEcg: "ECG",
  procNameAbdominalUltrasound: "Ecografía abdominal",
  procNamePreventiveCheckup: "Revisión preventiva",
  procNameMammography: "Mamografía",
  procNamePapSmear: "Citología (Papanicolaou)",
  procNameColorectalScreening: "Cribado de cáncer colorrectal",
  procNamePhysicalTherapy: "Fisioterapia",
  procNameVaccination: "Vacunación",
  procNameWoundCare: "Cura de heridas",
  procNameJointAspiration: "Artrocentesis",
  procNamePainInjection: "Tratamiento del dolor/infiltración",
  procNameColonoscopy: "Colonoscopia",
  procNameAppendectomy: "Apendicectomía",
  procNameGallbladderSurgery: "Cirugía de vesícula biliar",
  procNameKneeArthroscopy: "Artroscopia de rodilla",
  procNameCataractSurgery: "Cirugía de cataratas",
  procNameMastectomy: "Mastectomía",
  procNameHeadCt: "TC craneal",
  procNameSpineMri: "RM de columna",
  procNameEmergencyBloodTest: "Análisis de sangre urgente",
};

const pt: HealthConditionsStrings = {
  condTitle: "Doenças prévias",
  condEmpty: "Nenhuma doença documentada",
  condAdd: "Adicionar doença",
  condRemove: "Remover doença",
  condFieldCondition: "Doença",
  condCustomPlaceholder: "Introduzir doença",
  condIcd: "CID",
  condFieldCategory: "Categoria",
  condCategoryProblemListItem: "Doença crónica",
  condCategoryEncounterDiagnosis: "Diagnóstico agudo",
  condCategoryHealthConcern: "Risco para a saúde",
  condBadgeChronic: "Crónica",
  condBadgeAcute: "Aguda",
  condBadgeRisk: "Risco",
  condFieldClinicalStatus: "Estado clínico",
  condStatusActive: "Ativa",
  condStatusInactive: "Inativa",
  condStatusResolved: "Curada",
  condStatusRemission: "Em remissão",
  condStatusRecurrence: "Recorrência",
  condStatusRelapse: "Recaída",
  condFieldVerificationStatus: "Estado de verificação",
  condVerificationConfirmed: "Confirmado",
  condVerificationUnconfirmed: "Não confirmado",
  condVerificationProvisional: "Provisório",
  condVerificationDifferential: "Diagnóstico diferencial",
  condVerificationRefuted: "Excluído",
  condVerificationEnteredInError: "Registado por erro",
  condFieldSeverity: "Gravidade (opcional)",
  condSeverityNone: "Não especificada",
  condSeverityMild: "Ligeira",
  condSeverityModerate: "Moderada",
  condSeveritySevere: "Grave",
  condFieldOnset: "Diagnosticada pela primeira vez (opcional)",
  condFieldAbatement: "Curada/terminada em (opcional)",
  condFieldRecorder: "Médico assistente (opcional)",
  condRecorderPlaceholder: "Dr. Silva",
  condFieldNote: "Notas adicionais (opcional)",
  condNotePlaceholder: "Informações adicionais sobre a doença",
  condSince: "Desde: {date}",
  condUntil: "Até: {date}",
  condTreatingPhysician: "Médico assistente: {name}",
  condNameDiabetes: "Diabetes mellitus",
  condNameHypertension: "Hipertensão arterial",
  condNameAsthma: "Asma",
  condNameCoronaryArteryDisease: "Doença coronária",
  condNameGastritis: "Gastrite",
  condNameBackPain: "Dor nas costas",
  condNameDepression: "Depressão",
  condNameAnxietyDisorder: "Perturbação de ansiedade",
  condNameHighCholesterol: "Colesterol elevado",
  condNameRheumatoidArthritis: "Artrite reumatoide",
  condNameCopd: "DPOC",
  condNameKidneyDisease: "Insuficiência renal crónica",
  condNameConstipation: "Obstipação",
  condNameMyopia: "Miopia",
  condNameIronDeficiency: "Carência de ferro",
  condNameShingles: "Zona",

  treatFieldType: "Tipo de tratamento",
  treatTypeProcedure: "Exame",
  treatTypeTherapy: "Terapia",
  treatTypeSurgery: "Cirurgia",
  treatTypeMedication: "Medicação",
  treatStatusCompleted: "Concluído",
  treatStatusInProgress: "Em curso",
  treatStatusPlanned: "Planeado",
  treatStatusCancelled: "Cancelado",
  treatFieldTreatment: "Tratamento",
  treatCustomPlaceholder: "Introduzir tratamento",
  treatFieldDate: "Data",
  treatFieldPerformer: "Médico/terapeuta",
  treatPerformerPlaceholder: "Dr. Silva",
  treatFieldLocation: "Local do tratamento (opcional)",
  treatLocationPlaceholder: "Hospital, consultório, etc.",
  treatFieldOutcome: "Resultado/evolução (opcional)",
  treatOutcomePlaceholder: "Resultado do tratamento, achados, etc.",
  treatFieldNotes: "Notas adicionais (opcional)",
  treatNotesPlaceholder: "Informações adicionais",
  treatRemove: "Remover tratamento",
  treatAdd: "Adicionar tratamento",
  treatOutcomeLabel: "Resultado:",
  treatNameBloodPressureCheck: "Medição da tensão arterial",
  treatNameRoutineCheckup: "Exame de rotina",
  treatNameVaccination: "Vacinação",
  treatNameConsultation: "Consulta",
  treatNameBloodTest: "Análises ao sangue",
  treatNameXray: "Radiografia",
  treatNameUltrasound: "Ecografia",
  treatNamePhysicalTherapy: "Fisioterapia",
  treatNamePsychotherapy: "Psicoterapia",
  treatNameOccupationalTherapy: "Terapia ocupacional",
  treatNameRespiratoryTherapy: "Cinesiterapia respiratória",
  treatNameMassage: "Massagem",
  treatNameMinorSurgery: "Pequena cirurgia",
  treatNameArthroscopy: "Artroscopia",
  treatNameAppendectomy: "Apendicectomia",
  treatNameHeartSurgery: "Cirurgia cardíaca",
  treatNameAcetaminophenTherapy: "Tratamento com paracetamol",
  treatNameAntibioticCourse: "Tratamento com antibióticos",
  treatNameBloodPressureMedication: "Medicação para a tensão arterial",

  procFieldCategory: "Categoria",
  procCategoryOutpatient: "Ambulatório",
  procCategoryInpatient: "Internamento",
  procCategoryAmbulatory: "Hospital de dia",
  procCategoryEmergency: "Urgência",
  procStatusCompleted: "Concluído",
  procStatusInProgress: "Em curso",
  procStatusPreparation: "Em preparação",
  procStatusNotDone: "Não realizado",
  procStatusOnHold: "Em pausa",
  procStatusStopped: "Interrompido",
  procStatusEnteredInError: "Registado por erro",
  procStatusUnknown: "Desconhecido",
  procFieldProcedure: "Intervenção/exame",
  procCustomPlaceholder: "Introduzir intervenção/exame",
  procFieldDate: "Data",
  procFieldPerformer: "Médico/instituição",
  procPerformerPlaceholder: "Dr. Silva / Hospital Universitário",
  procFieldLocation: "Local (opcional)",
  procLocationPlaceholder: "Hospital, consultório, clínica",
  procFieldReason: "Indicação/motivo (opcional)",
  procReasonPlaceholder: "Motivo da intervenção/do exame",
  procFieldOutcome: "Resultado/achados (opcional)",
  procOutcomePlaceholder: "Achados, resultado do exame/da intervenção",
  procFieldComplications: "Complicações (opcional)",
  procComplicationsPlaceholder: "Complicações ocorridas",
  procFieldNotes: "Notas adicionais (opcional)",
  procNotesPlaceholder: "Informações adicionais",
  procRemove: "Remover intervenção",
  procAdd: "Adicionar intervenção",
  procBadgeUpcoming: "Agendado",
  procBadgeNew: "Novo",
  procNoDate: "Sem data",
  procLocationLabel: "Local:",
  procIndicationLabel: "Indicação:",
  procOutcomeLabel: "Resultado:",
  procComplicationsLabel: "Complicações:",
  procNameChestXray: "Radiografia do tórax",
  procNameBloodPanel: "Análises ao sangue completas",
  procNameEcg: "ECG",
  procNameAbdominalUltrasound: "Ecografia abdominal",
  procNamePreventiveCheckup: "Exame preventivo",
  procNameMammography: "Mamografia",
  procNamePapSmear: "Citologia (Papanicolau)",
  procNameColorectalScreening: "Rastreio do cancro colorretal",
  procNamePhysicalTherapy: "Fisioterapia",
  procNameVaccination: "Vacinação",
  procNameWoundCare: "Tratamento de feridas",
  procNameJointAspiration: "Artrocentese",
  procNamePainInjection: "Tratamento da dor/infiltração",
  procNameColonoscopy: "Colonoscopia",
  procNameAppendectomy: "Apendicectomia",
  procNameGallbladderSurgery: "Cirurgia da vesícula biliar",
  procNameKneeArthroscopy: "Artroscopia do joelho",
  procNameCataractSurgery: "Cirurgia de cataratas",
  procNameMastectomy: "Mastectomia",
  procNameHeadCt: "TAC crânio-encefálica",
  procNameSpineMri: "Ressonância magnética da coluna",
  procNameEmergencyBloodTest: "Análises ao sangue urgentes",
};

export const healthConditionsStrings: Record<
  Language,
  HealthConditionsStrings
> = {
  en,
  de,
  fr,
  es,
  pt,
};

/**
 * Whether `display` is one of the built-in labels for `key` in any language
 * (or one of the extra legacy spellings). Older versions stored the German
 * label as the record's display text, so a record whose text is any of these
 * is shown in the active language instead.
 */
export function isBuiltInLabel(
  key: keyof HealthConditionsStrings,
  display: string | undefined,
  legacy: readonly string[] = [],
): boolean {
  if (!display) return true;
  const value = display.trim();
  return (
    legacy.includes(value) ||
    Object.values(healthConditionsStrings).some((lang) => lang[key] === value)
  );
}
